import { produce } from 'immer';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  DEBOUNCE_TIME,
  DefaultDescriptionBenefitEducation,
  FormBase,
  type IBaseModalProps,
  INIT_PAGE,
  IPaginationMetaProps,
  Logger,
  formatUnknownErrorMessage,
  useToast,
} from '@gbs-monorepo-packages/common';
import {
  type FontsListFormSchema,
  fontsListFormSchema,
} from '@gbs-monorepo/edge/formSchemas/courseSchema';
import { zodResolver } from '@hookform/resolvers/zod';

import { FormPaginationModal } from '../../../../components/FormPaginationModal';
import {
  type ICourseDTO,
  type IFontDTO,
  getCourseFonts,
} from '../../../../services/courses';
import { Loading } from '../GlobalStyleModal/styles';
import { SelectFonts } from '../SelectFonts';
import { Fieldset, Input, SelectContainer } from './styles';

export interface IFont extends IFontDTO {
  selected: boolean;
  index: number;
}

interface IAddPageModalProps extends Partial<
  Omit<IBaseModalProps, 'children' | 'onOpenChange'>
> {
  courseId: number;
  courseFonts: IFontDTO[];
  onAccept: (fonts: Pick<ICourseDTO, 'fonts'>) => Promise<void>;
  onDecline: () => void;
  loading?: boolean;
}

export const FontsModal = ({
  open = false,
  courseId,
  courseFonts,
  onAccept,
  onDecline,
  loading,
  ...props
}: IAddPageModalProps): JSX.Element => {
  const { addToast } = useToast();

  const [orderedCheckedFonts, setOrderedCheckedFonts] = useState<IFont[]>([]);
  const [loadingFonts, setLoadingFonts] = useState(true);
  const [loadingSaveFont, setLoadingSaveFont] = useState(false);

  const [search, setSearch] = useState('');
  const [paginationMeta, setPaginationMeta] = useState<IPaginationMetaProps>();
  const refFormModalContent = useRef<HTMLDivElement>(null);

  const newSelectedFontsSet = useRef(new Set<string>());
  const didInitSelectedFontsFromCourse = useRef(false);
  const fontsRequestInFlightRef = useRef(false);
  const lastFontsRequestKeyRef = useRef<string | null>(null);

  const fontsForm = useForm<FontsListFormSchema>({
    resolver: zodResolver(fontsListFormSchema),
    mode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      fonts: [],
    },
  });

  const { setValue, watch, reset } = fontsForm;

  const values = watch();

  useEffect(() => {
    if (open) {
      reset({
        fonts: [],
      });
      setOrderedCheckedFonts([]);
      setSearch('');
      newSelectedFontsSet.current.clear();
      didInitSelectedFontsFromCourse.current = false;
      fontsRequestInFlightRef.current = false;
      lastFontsRequestKeyRef.current = null;
      setPaginationMeta(undefined);
    }
  }, [open, reset]);

  const getFonts = useCallback(
    async (id: number, page: number, limit: number, filter = '') => {
      const requestKey = `${id}:${page}:${limit}:${filter}`;
      if (fontsRequestInFlightRef.current) return;
      if (lastFontsRequestKeyRef.current === requestKey) return;

      fontsRequestInFlightRef.current = true;
      lastFontsRequestKeyRef.current = requestKey;
      setLoadingFonts(true);

      try {
        const response = await getCourseFonts({ id, page, limit, filter });
        if (!didInitSelectedFontsFromCourse.current) {
          courseFonts.forEach((font) =>
            newSelectedFontsSet.current.add(font.family)
          );
          didInitSelectedFontsFromCourse.current = true;
        }
        const resultWithIndex = response.data.map((data, index) => ({
          ...data,
          selected: newSelectedFontsSet.current.has(data.family),
          index,
        }));

        const fontDict = new Map(
          fontsForm.getValues('fonts').map((font) => [font.family, font])
        );

        resultWithIndex.forEach((font) => {
          fontDict.set(font.family, font);
        });

        setValue('fonts', Array.from(fontDict.values()), {
          shouldValidate: true,
        });
        setOrderedCheckedFonts((prev) => [
          ...prev,
          ...resultWithIndex.filter(
            (font) =>
              font.selected &&
              !prev.some((prevFont) => prevFont.family === font.family)
          ),
        ]);
        setPaginationMeta(response.meta);
      } catch (error: unknown) {
        lastFontsRequestKeyRef.current = null;
        Logger.debug('error: ', error);
        addToast({
          title: 'Error on load fonts',
          description: formatUnknownErrorMessage(
            error,
            DefaultDescriptionBenefitEducation
          ),
          styleType: 'error',
          dataCy: 'error-toast',
          duration: 3000,
        });
      } finally {
        fontsRequestInFlightRef.current = false;
        setLoadingFonts(false);
      }
    },
    [addToast, courseFonts, fontsForm, setValue]
  );

  useEffect(() => {
    if (!open) return;
    const debounceGetFonts = debounce(() => {
      void getFonts(courseId, INIT_PAGE, 30, search);
    }, DEBOUNCE_TIME);

    debounceGetFonts();

    return () => {
      debounceGetFonts.cancel();
    };
  }, [search, getFonts, courseId, open]);

  const searchFonts = useCallback(
    (searchByButton = false) => {
      if (!open) return;
      if (search && values.fonts.length !== 0) return;
      if (!loadingFonts || searchByButton) {
        const pageAux = search
          ? 0
          : Number((paginationMeta?.page ?? 0) > 0 ? paginationMeta?.page : 0);
        void getFonts(courseId, pageAux + 1, 30, search);
      }
    },
    [
      open,
      search,
      values.fonts,
      loadingFonts,
      paginationMeta?.page,
      getFonts,
      courseId,
    ]
  );

  const handleDeclineAddFonts = useCallback(() => {
    const resultWithIndex = courseFonts.map((data, index) => ({
      ...data,
      selected: orderedCheckedFonts.some((font) => font.family === data.family),
      index,
    }));

    newSelectedFontsSet.current.clear();
    orderedCheckedFonts.forEach((font) =>
      newSelectedFontsSet.current.add(font.family)
    );

    setOrderedCheckedFonts(resultWithIndex.filter((font) => font.selected));
    setValue('fonts', resultWithIndex, { shouldValidate: true });
    setSearch('');
    reset({
      fonts: [],
    });
    onDecline?.();
  }, [courseFonts, orderedCheckedFonts, onDecline, setValue, reset]);

  const handleSelectedNewFonts = useCallback(
    (newChecked: IFont) => {
      const currentFonts = fontsForm.getValues('fonts') as IFont[];
      const currentFont = currentFonts.find(
        (font) => font.family === newChecked.family
      );
      const nextSelected = !(currentFont?.selected ?? false);

      setValue(
        'fonts',
        currentFonts.map((font) => {
          if (font.family !== newChecked.family) {
            return font;
          }

          if (nextSelected) {
            newSelectedFontsSet.current.add(newChecked.family);
          } else {
            newSelectedFontsSet.current.delete(newChecked.family);
          }

          return { ...font, selected: nextSelected };
        }),
        { shouldValidate: true }
      );

      setOrderedCheckedFonts(
        produce((draft) => {
          const index = draft.findIndex(
            (font) => font.family === newChecked.family
          );

          if (!nextSelected) {
            if (index !== -1) draft.splice(index, 1);
            return;
          }

          if (index === -1) {
            draft.push({ ...newChecked, selected: true });
          }
        })
      );
    },
    [fontsForm, setValue]
  );

  const handleAcceptAddFonts = useCallback(async () => {
    setLoadingSaveFont(true);
    try {
      await onAccept?.({ fonts: orderedCheckedFonts });
      reset({
        fonts: [],
      });
    } catch (error: unknown) {
      Logger.debug('error saving fonts: ', error);
    } finally {
      setLoadingSaveFont(false);
    }
  }, [onAccept, orderedCheckedFonts, reset]);

  return (
    <FormBase.Provider {...fontsForm}>
      <FormPaginationModal
        {...props}
        mainText="Select the course fonts"
        dataCy="title-fonts"
        acceptText="Confirm"
        declineText="Cancel"
        onAccept={() => void handleAcceptAddFonts()}
        onDecline={handleDeclineAddFonts}
        onOpenChange={handleDeclineAddFonts}
        fullWidth
        open={open}
        loading={loadingSaveFont || loading}
        refContent={refFormModalContent}
        zIndex={4}
      >
        <p data-cy="description-fonts">
          Select the fonts you would like to use.
          {` (${orderedCheckedFonts.length} selected)`}
        </p>
        <Fieldset>
          <Input
            id="search-fonts"
            data-cy="input-searchFonts"
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              e.target.value.length <= 20 && setSearch(e.target.value)
            }
            placeholder="Search"
            type="text"
            disabled={loadingFonts}
          />
        </Fieldset>
        <SelectContainer data-cy="fonts-content">
          {!paginationMeta && loadingFonts ? (
            <Loading />
          ) : (
            <SelectFonts
              fonts={values.fonts as IFont[]}
              onValueChange={handleSelectedNewFonts}
              value={orderedCheckedFonts}
              search={search}
              paginationMeta={paginationMeta}
              searchFonts={searchFonts}
              loading={loadingFonts}
            />
          )}
        </SelectContainer>
      </FormPaginationModal>
    </FormBase.Provider>
  );
};
